import { BWS_PICKUP_LOCATIONS } from '@bcube/shared/constants';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Copy, Edit, Loader2, Plus, Trash } from 'lucide-react';
import { useRef, useState } from 'react';
import { type AccountAddress, accountApi } from '@/api/commercial/accounts';
import { type Proposal, proposalApi, type UpdateProposalPayload } from '@/api/commercial/proposals';
import { AddressFormDialog } from '@/components/commercial/accounts/create/address-form-dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { FormError } from '@/components/ui/form-error';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import type { LocalAddress } from '@/context/create-account-flow';
import { useFormError } from '@/hooks/use-form-error';
import { useToast } from '@/hooks/use-toast';
import { getErrorMessage } from '@/lib/error-message';
import { cn } from '@/lib/utils';

interface StepDeliveryProps {
  accountId: string | undefined;
  proposalId?: string;
  proposal?: Proposal | null;
  onSavingChange?: (saving: boolean) => void;
}

function formatAddressParts(address: AccountAddress) {
  const parts: string[] = [];
  if (address.street && address.number && address.district) {
    parts.push(`${address.street}, ${address.number}, ${address.district}`);
  }
  if (address.city && address.state && address.zip_code) {
    parts.push(`${address.city} - ${address.state}, ${address.zip_code}`);
  }
  return parts;
}

export function StepDelivery({
  accountId,
  proposalId,
  proposal,
  onSavingChange,
}: StepDeliveryProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { formError, showFormError, clearFormError } = useFormError(proposalId);
  const selectedAddressId = proposal?.deliveryAddressId ?? null;
  const mode = proposal?.deliveryMode ?? (selectedAddressId ? 'CLIENT' : null);
  const inFlight = useRef(false);
  const editable = proposal?.status === 'OPEN';

  const [addressDialogOpen, setAddressDialogOpen] = useState(false);
  const [editingAddress, setEditingAddress] = useState<LocalAddress | null>(null);
  const [deleteAddressId, setDeleteAddressId] = useState<string | null>(null);

  const {
    data: addresses = [],
    isLoading,
    isError: addressLoadFailed,
    error: addressLoadError,
    refetch,
    isFetching,
  } = useQuery({
    queryKey: ['addresses', accountId],
    queryFn: () => accountApi.findAddresses(accountId!),
    enabled: !!accountId,
  });

  const createAddressMutation = useMutation({
    mutationFn: (data: LocalAddress) => {
      if (!accountId) throw new Error('Account ID is required');
      return accountApi.createAddress({ ...data, accountId });
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Endereço adicionado',
        description: 'O endereço foi salvo com sucesso!',
      });
      queryClient.invalidateQueries({ queryKey: ['addresses', accountId] });
    },
  });

  const updateAddressMutation = useMutation({
    mutationFn: ({ addressId, data }: { addressId: string; data: LocalAddress }) => {
      return accountApi.updateAddress(addressId, { ...data, accountId: accountId! });
    },
    onSuccess: () => {
      toast({
        variant: 'success',
        title: 'Endereço atualizado',
        description: 'O endereço foi atualizado com sucesso!',
      });
      queryClient.invalidateQueries({ queryKey: ['addresses', accountId] });
    },
  });

  const saveDeliveryAddressMutation = useMutation({
    mutationFn: (payload: UpdateProposalPayload) => {
      if (!proposalId) throw new Error('Proposal ID is required');
      return proposalApi.setDelivery(proposalId, payload);
    },
    onSuccess: async (data) => {
      clearFormError();
      queryClient.setQueryData(['proposal', proposalId], data);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ['cart-calculate', proposalId] }),
        queryClient.invalidateQueries({ queryKey: ['proposals'] }),
      ]);
    },
    onError: (error) => {
      showFormError(error);
      toast({
        title: 'Erro',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
    onSettled: () => {
      inFlight.current = false;
      onSavingChange?.(false);
    },
  });

  const saveDestination = (payload: UpdateProposalPayload) => {
    if (inFlight.current || !editable) return;
    inFlight.current = true;
    onSavingChange?.(true);
    saveDeliveryAddressMutation.mutate(payload);
  };
  const busy = saveDeliveryAddressMutation.isPending;

  const deleteAddressMutation = useMutation({
    onMutate: () => {
      inFlight.current = true;
      onSavingChange?.(true);
    },
    onSettled: () => {
      inFlight.current = false;
      onSavingChange?.(false);
    },
    mutationFn: async (addressId: string) => {
      if (selectedAddressId === addressId && proposalId) {
        const updated = await proposalApi.setDelivery(proposalId, {
          deliveryMode: 'CLIENT',
          deliveryAddressId: null,
        });
        queryClient.setQueryData(['proposal', proposalId], updated);
      }
      return accountApi.deleteAddress(addressId);
    },
    onSuccess: (_data, _deletedId) => {
      clearFormError();
      toast({
        variant: 'success',
        title: 'Endereço removido',
        description: 'O endereço foi excluído com sucesso!',
      });
      queryClient.invalidateQueries({ queryKey: ['addresses', accountId] });
      setDeleteAddressId(null);
    },
    onError: (error) => {
      showFormError(error);
      toast({
        title: 'Erro',
        description: getErrorMessage(error),
        variant: 'destructive',
      });
    },
  });

  const handleSubmitAddress = async (localAddress: LocalAddress) => {
    if (editingAddress) {
      await updateAddressMutation.mutateAsync({ addressId: editingAddress.id, data: localAddress });
    } else {
      await createAddressMutation.mutateAsync(localAddress);
    }
    setAddressDialogOpen(false);
    setEditingAddress(null);
  };

  const handleOpenCreateDialog = () => {
    setEditingAddress(null);
    setAddressDialogOpen(true);
  };

  const handleOpenEditDialog = (address: LocalAddress) => {
    setEditingAddress(address);
    setAddressDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setAddressDialogOpen(false);
    setEditingAddress(null);
  };

  const handleCopyAddress = async (address: AccountAddress) => {
    try {
      const { street, number, district, city, state, zip_code, complement, reference_point } =
        address;
      let text = `${street}, ${number}, ${district}\n${city} - ${state}, ${zip_code}`;
      if (complement) text += `\nComplemento: ${complement}`;
      if (reference_point) text += `\nPonto de Referência: ${reference_point}`;
      await navigator.clipboard.writeText(text);
      toast({
        variant: 'success',
        title: 'Sucesso!',
        description: 'Endereço copiado para a área de transferência.',
      });
    } catch {
      toast({
        title: 'Erro',
        description: 'Não foi possível copiar o endereço.',
        variant: 'destructive',
      });
    }
  };

  const handleSelectAddress = (addressId: string) => {
    if (busy || !editable || deleteAddressMutation.isPending) return;
    const newAddressId = selectedAddressId === addressId ? null : addressId;
    saveDestination({ deliveryMode: 'CLIENT', deliveryAddressId: newAddressId });
  };

  const handleDeleteAddress = (addressId: string) => {
    setDeleteAddressId(addressId);
  };

  if (!accountId) {
    return (
      <div className="flex items-center justify-center rounded-lg border border-dashed border-gray-300 py-12">
        <p className="text-sm text-muted-foreground">
          Selecione um cliente antes de gerenciar endereços.
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const isEmpty = !addressLoadFailed && addresses.length === 0;

  return (
    <>
      <FormError error={formError} onDismiss={clearFormError} />
      {saveDeliveryAddressMutation.isError && saveDeliveryAddressMutation.variables && (
        <Button
          type="button"
          variant="outline"
          disabled={busy || !editable || deleteAddressMutation.isPending}
          onClick={() => {
            if (saveDeliveryAddressMutation.variables)
              saveDestination(saveDeliveryAddressMutation.variables);
          }}
        >
          Tentar salvar destino novamente
        </Button>
      )}
      {addressLoadFailed && (
        <div role="alert" className="space-y-2 text-sm text-destructive">
          <p>Não foi possível carregar os endereços do cliente.</p>
          <p>{getErrorMessage(addressLoadError)}</p>
          <Button
            type="button"
            variant="outline"
            disabled={isFetching}
            onClick={() => void refetch()}
          >
            Tentar carregar endereços novamente
          </Button>
        </div>
      )}
      <fieldset
        disabled={busy || !editable || deleteAddressMutation.isPending}
        className="space-y-4"
      >
        <legend className="sr-only">Modalidade de entrega</legend>
        <div className="flex gap-4" role="radiogroup" aria-label="Modalidade de entrega">
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="delivery-mode"
              checked={mode === 'CLIENT'}
              onChange={() => saveDestination({ deliveryMode: 'CLIENT', deliveryAddressId: null })}
            />
            Enviar para o cliente
          </label>
          <label className="flex items-center gap-2">
            <input
              type="radio"
              name="delivery-mode"
              checked={mode === 'BWS'}
              onChange={() => saveDestination({ deliveryMode: 'BWS', pickupLocationId: null })}
            />
            Retirar na BWS
          </label>
        </div>
        {busy && <output className="block">Salvando destino...</output>}
        {!editable && <p>Reabra a proposta para alterar a entrega.</p>}
        {!mode ? (
          <p className="text-sm text-muted-foreground">
            Escolha se o pedido será enviado ao cliente ou retirado na BWS.
          </p>
        ) : mode === 'BWS' ? (
          <div className="space-y-4">
            <p>Frete: R$ 0,00.</p>
            <label className="block space-y-2">
              Endereço do cliente para faturamento
              <select
                className="block w-full rounded border p-2"
                aria-label="Referência fiscal do cliente"
                aria-describedby="fiscal-address-help"
                value={proposal?.fiscalAddressId ?? ''}
                onChange={(event) =>
                  saveDestination({
                    deliveryMode: 'BWS',
                    fiscalAddressId: event.target.value || null,
                  })
                }
              >
                <option value="">Selecione um endereço do cliente</option>
                {addresses.map((address) => (
                  <option key={address.id} value={address.id}>
                    {formatAddressParts(address).join(' - ')}
                  </option>
                ))}
              </select>
            </label>
            <p id="fiscal-address-help" className="text-sm text-muted-foreground">
              Para enviar a proposta, é necessário selecionar um endereço fiscal do cliente, mesmo
              quando a retirada for na BWS.
            </p>
            {isEmpty && (
              <p>Cadastre um endereço na conta do cliente para informar a referência fiscal.</p>
            )}
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {BWS_PICKUP_LOCATIONS.map((unit) => {
                const selected = proposal?.pickupLocationId === unit.id;
                const address = unit.address;
                return (
                  <Card
                    key={unit.id}
                    className={cn(
                      'p-4 space-y-3 transition-colors',
                      address && editable && !busy && 'cursor-pointer hover:bg-primary/5',
                      selected && 'border-2 border-primary bg-primary/5',
                    )}
                    onClick={(event) => {
                      if ((event.target as HTMLElement).closest('label, button, input')) return;
                      if (!address || busy || !editable || deleteAddressMutation.isPending) return;
                      saveDestination({
                        deliveryMode: 'BWS',
                        pickupLocationId: selected ? null : unit.id,
                      });
                    }}
                  >
                    <label
                      htmlFor={`pickup-location-${unit.id}`}
                      className="flex cursor-pointer items-center justify-between font-medium"
                    >
                      {unit.name}
                      <Checkbox
                        id={`pickup-location-${unit.id}`}
                        aria-label={`Selecionar unidade ${unit.name}`}
                        checked={selected}
                        disabled={!address || busy || !editable}
                        onCheckedChange={() =>
                          saveDestination({
                            deliveryMode: 'BWS',
                            pickupLocationId: selected ? null : unit.id,
                          })
                        }
                      />
                    </label>
                    {address ? (
                      <>
                        {address.provisional && (
                          <Badge variant="outline">
                            Endereço provisório — aguardando endereço oficial
                          </Badge>
                        )}
                        <p>
                          {address.street}, {address.number}, {address.district}
                          <br />
                          {address.city}/{address.state}, {address.zip_code}
                        </p>
                        {address.complement && <p>Complemento: {address.complement}</p>}
                        <Button
                          type="button"
                          variant="ghost"
                          aria-label={`Copiar endereço ${unit.name}`}
                          onClick={(event) => {
                            event.stopPropagation();
                            handleCopyAddress({ ...address, id: unit.id } as AccountAddress);
                          }}
                        >
                          <Copy className="mr-2 h-4 w-4" />
                          Copiar
                        </Button>
                      </>
                    ) : (
                      <output className="block">{unit.unavailableReason}</output>
                    )}
                  </Card>
                );
              })}
            </div>
          </div>
        ) : isEmpty ? (
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              Ainda não há endereços cadastrados para este cliente!
            </p>
            <Button
              className="bg-black text-white hover:bg-gray-800"
              onClick={handleOpenCreateDialog}
            >
              <Plus className="mr-2 h-4 w-4" />
              Adicionar endereço
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">Selecione um endereço de entrega:</p>
              <Button
                className="bg-black text-white hover:bg-gray-800"
                onClick={handleOpenCreateDialog}
              >
                <Plus className="mr-2 h-4 w-4" />
                Adicionar endereço
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {addresses.map((address) => {
                const isSelected = selectedAddressId === address.id;
                const addressParts = formatAddressParts(address);
                const isFiscalApi = address.type?.includes('Fiscal') ?? false;

                return (
                  <Card
                    key={address.id}
                    className={cn(
                      'flex flex-col p-4 rounded-lg transition-all duration-200 cursor-pointer',
                      isSelected
                        ? 'border-2 border-primary shadow-md bg-primary/5'
                        : 'border border-gray-200 shadow-sm hover:shadow-md',
                    )}
                    onClick={() => handleSelectAddress(address.id)}
                  >
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="flex flex-wrap gap-1">
                        {address.type &&
                          address.type.length > 0 &&
                          address.type.map((type) => (
                            <Badge key={type} variant="outline" className="text-xs font-medium">
                              {type}
                            </Badge>
                          ))}
                      </div>
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => handleSelectAddress(address.id)}
                        onClick={(e) => e.stopPropagation()}
                        aria-label={`Selecionar endereço ${address.street}`}
                      />
                    </div>

                    <div className="flex-1 space-y-2">
                      {addressParts.map((part, index) => (
                        <div
                          key={part}
                          className={
                            index === 0 ? 'font-medium text-sm' : 'text-sm text-muted-foreground'
                          }
                        >
                          {part}
                        </div>
                      ))}

                      {(address.complement || address.reference_point) && (
                        <>
                          <Separator className="my-3" />
                          <div className="space-y-1">
                            {address.complement && (
                              <div className="text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">Complemento:</span>{' '}
                                {address.complement}
                              </div>
                            )}
                            {address.reference_point && (
                              <div className="text-xs text-muted-foreground">
                                <span className="font-medium text-foreground">
                                  Ponto de Referência:
                                </span>{' '}
                                {address.reference_point}
                              </div>
                            )}
                          </div>
                        </>
                      )}
                    </div>

                    <div className="flex items-center justify-end gap-1 mt-3 pt-3 border-t border-gray-100">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="h-8 w-8 text-muted-foreground hover:text-foreground"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleCopyAddress(address);
                              }}
                              aria-label="Copiar endereço"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                          </TooltipTrigger>
                          <TooltipContent side="top">
                            <p>Copiar</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>

                      {!isFiscalApi && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-foreground"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenEditDialog(address);
                                }}
                                aria-label="Editar endereço"
                              >
                                <Edit className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>Editar</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}

                      {!isFiscalApi && (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <Button
                                variant="ghost"
                                size="icon"
                                className="h-8 w-8 text-muted-foreground hover:text-destructive"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteAddress(address.id);
                                }}
                                aria-label="Excluir endereço"
                              >
                                <Trash className="h-4 w-4" />
                              </Button>
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p>Excluir</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                  </Card>
                );
              })}
            </div>
          </div>
        )}
      </fieldset>
      <AddressFormDialog
        open={addressDialogOpen}
        onClose={handleCloseDialog}
        onSubmit={handleSubmitAddress}
        mode={editingAddress ? 'edit' : 'create'}
        address={editingAddress ?? undefined}
      />

      <AlertDialog
        open={!!deleteAddressId}
        onOpenChange={(open) => !open && setDeleteAddressId(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir endereço</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir este endereço? Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 text-white hover:bg-red-700"
              disabled={busy || deleteAddressMutation.isPending}
              onClick={() => {
                if (deleteAddressId && !inFlight.current) {
                  inFlight.current = true;
                  deleteAddressMutation.mutate(deleteAddressId);
                }
              }}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
